import 'reflect-metadata';
import { type ReactElement } from 'react';
import { Route } from 'react-router';
import { container, injectAll, singleton } from 'tsyringe';
import { FeatureToken } from '@app/constants/di';
import { ENV } from '@app/constants/env';
import type {
  FeatureSetupDef,
  FeatureInfo,
  FeatureRoute,
  FeatureSetup,
  FeatureRolePermissions,
  EnvironmentsDef,
} from '@app/types/feature.types';

const ENVIRONMENT = ENV.ENVIRONMENT === 'local' ? 'dev' : ENV.ENVIRONMENT;

@singleton()
export class FeatureInitializer {
  private readonly _features: FeatureSetupDef[] = [];

  private readonly _featureNamespaces: string[] = [];

  private readonly _applicationRolePermissionMappings: FeatureRolePermissions[] =
    [];

  constructor (@injectAll(FeatureToken) features?: FeatureSetupDef[]) {
    if (features) {
      const duplicates = this.checkDuplicateNamespaces(features);
      if (duplicates.length > 0) {
        console.error(`DUPLICATED FEATURES: ${duplicates.join(', ')}`);
        return;
      }
      this._features = features;
      this.run(this.createNamespaces);
      this.run(this.createPermissions);
    }
  }

  private readonly checkDuplicateNamespaces = (features: FeatureSetupDef[]) => {
    const duplicates: string[] = [];
    const namespaces = features.reduce<string[]>((all, current) => {
      all.push(current.GetFeatureSetup().namespace);
      return all;
    }, []);
    namespaces.forEach((namespace, index, array) => {
      if (
        array.includes(namespace, index + 1) &&
        !duplicates.includes(namespace)
      ) {
        duplicates.push(namespace);
      }
    });
    return duplicates;
  };

  private readonly run = <T,>(factoryFn: (setup: FeatureSetup) => T) => {
    return this._features.flatMap((currentSetup) => {
      const featureSetup = currentSetup.GetFeatureSetup();
      return featureSetup.featuresRoot.removeInEnvironment?.includes(ENVIRONMENT as EnvironmentsDef)
        ? []
        : factoryFn(featureSetup);
    });
  };

  // #region Namespaces
  public Namespaces = this._featureNamespaces;

  private readonly createNamespaces = (setup: FeatureSetup) => {
    this._featureNamespaces.push(setup.namespace);
  };
  // #endregion

  // #region Permissions
  public Permissions = this._applicationRolePermissionMappings;

  private readonly createPermissions = (setup: FeatureSetup) => {
    this._applicationRolePermissionMappings.push({
      namespace: setup.namespace,
      rolePermissions: Object.entries(setup.permissions).reduce(
        (rolePermissions) => ({
          ...rolePermissions,
        }),
        {},
      ),
    });
  };
  // #endregion

  // #region Routes
  public Routes = () => this.run(this.createFeatureRoot);

  private readonly createFeatureRoot = (setup: FeatureSetup) => {
    return this.createFeaturesRoutes(setup.featuresRoot.children ?? [], setup);
  };

  private readonly createFeaturesRoutes = (
    featureRoutes: FeatureRoute[],
    featureInfo: FeatureInfo,
  ) => {
    return featureRoutes.reduce<ReactElement[]>(
      (allFeatureRoutes, currentFeatureRoute) => {
        allFeatureRoutes.push(this.createFeatureRoute(currentFeatureRoute, featureInfo));
        if (currentFeatureRoute.children) allFeatureRoutes.push(...this.createFeaturesRoutes(
          currentFeatureRoute.children,
          featureInfo,
        ));

        return allFeatureRoutes;
      },
      [],
    );
  };

  private readonly createFeatureRoute = (
    featureRoute: FeatureRoute,
    featureInfo: FeatureInfo,
  ) => {
    return (
      <Route
        key={featureRoute.path}
        path={
          featureInfo.index
            ? featureRoute.path
            : `${featureInfo.namespace}${featureRoute.path}`
        }
        element={
          <featureRoute.page
            title={featureRoute.title}
            requiredPermissions={featureRoute.requiredPermissions ?? []}
          />
        }
      />
    );
  };
}

const features = container.resolve(FeatureInitializer);

export const appRoutes = () => features.Routes();
export const appRolePermissionMappings = () => features.Permissions;
